import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  Button,
  TextField,
  MenuItem,
  Select,
  Typography,
  Box,
  Divider,
  LinearProgress,
  DialogContent,
  DialogActions,
  FormControlLabel,
  FormControl,
  InputLabel,
  Switch,
  Stack,
  DialogTitle,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import TimezoneSelect from "react-timezone-select";
import { useSelector, useDispatch } from "react-redux";
import {
  convertUtcToTimezone,
  convertUTCToTimezone,
  convertUTCToTimezoneNormalFormat,
  extractTimezone,
  formatDate,
  formatTimeZone,
  getTimeZoneWithGMT,
} from "../../utils/format_date";
import {
  getConfigureStageTransfer,
  getDealStageOverRideView,
  getDealTemplates,
  overRidingTimeFrame,
  postConfigureStageTransfer,
  updateConfigureStageTransfer,
} from "../../redux/modules/deals";
import { setAlert } from "../../redux/modules/alert";
import { Dialog } from "@mui/material";
import { AddCircleOutline } from "@mui/icons-material";
import DealEmailTemplate from "../../containers/Settings/components/DealEmailTemplate";
import dayjs from "dayjs";
import { formatTime, parseTimeUnit } from "../../utils/timeConverter";
import { getBTemplates } from "../../redux/modules/email";
import CloseIcon from "@mui/icons-material/Close";
import FileUploader from "../FileUploader/FileUploader";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

const DealConfigure = ({
  cancel,
  deal,
  renderDeal,
  setRenderDeal,
  dealDetails,
  setDealStageRuleData,
  dealStageRuleData,
}) => {
  const dispatch = useDispatch();

  const [selectedTimezone, setSelectedTimezone] = useState({ value: "" });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedFromStage, setSelectedFromStage] = useState({
    stage_id: "",
    stage_name: "",
  });
  const [selectedToStage, setSelectedToStage] = useState({
    stage_id: "",
    stage_name: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [fromId, setFromId] = useState(null);
  const [count, setCount] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [dealConfigureData, setDealConfigureData] = useState([]);
  const [editId, setEditId] = useState(null);
  const [disable, setDisable] = useState(true);
  const [timeZoneFormat, setTimezoneFormat] = useState({ value: "" });
  const [toStages, setToStage] = useState([]);
  const [fromStages, setFromStage] = useState([]);
  const [loadingLinear, setLoadingLinear] = useState(true);
  const [isChanged, setIsChanged] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);
  const [sameStageCheck, setSameStageCheck] = useState(null);
  const [overRideOpen, setOverRideOpen] = useState(false);
  const [isSendMail, setIsSendMail] = useState(true);
  const [openTemplate, setOpenTemplate] = useState(false);
  const [loading, setLoading] = useState(false);
  const bodyTemplates = useSelector((state) => state?.email?.bodyTemplates);
  const pipelines = useSelector((state) => state.deals.pipelines);
  const [touched, setTouched] = useState(false);
  const [formState, setFormState] = useState({
    moveAfterValue: "",
    moveAfterUnit: "",
    selectedEmailTemplate: "",
    sendEmailAfterValue: "",
    sendEmailAfterUnit: "",
    senderName: "",
  });
  const [emailConfigData, setEmailConfigData] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [deletedFileIds, setDeletedFileIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const dealPipelineId = deal?.deal_pipeline;
  const currentTZ = useSelector((state) => state.auth.timezone);
  const filteredPipeline = pipelines.filter(
    (pipeline) => pipeline.pipeline_id === dealPipelineId
  );
  const pipelineStages =
    filteredPipeline?.length > 0 ? filteredPipeline[0]?.pipeline_stages : [];
  const dealId = deal?.deal_id;

  const fromStage = pipelineStages?.filter(
    (_, index) =>
      index >
      pipelineStages.findIndex((stage) => stage.stage_id === deal?.deal_stage)
  );

  const currentDealStage = deal?.deal_stage;

  const handleStageChange = (type, stage_id, stage_name) => {
    if (type === "from") {
      setSelectedFromStage({ stage_id, stage_name });
      setFromId(stage_id);
      setDisable(false);
      setSelectedToStage({ stage_id: "", stage_name: "" });

      // Filter out the selected fromStage from the toStage options
      const updatedToStage = fromStages.filter(
        (stage) => stage.stage_id !== stage_id
      );
      setToStage(updatedToStage); // Set the filtered toStage list
    } else {
      setSelectedToStage({ stage_id, stage_name });
      setIsFormModified(true);
    }
    setErrorMessage(""); // Clear error if user is selecting a new stage
  };

  useEffect(() => {
    if (dealPipelineId) {
      dispatch(getConfigureStageTransfer(dealPipelineId, dealId))
        .then((res) => {
          const { result } = res;
          setDealConfigureData(result);
          setIsChanged(false);
        })
        .catch((err) => {
          if (err.response && err.response.status === 404) {
            // Handle not found (no configuration)
            setDealConfigureData([]);
            setIsChanged(false);
          } else {
            console.error("Unexpected error:", err);
          }
        });
    }
  }, [dealPipelineId, count]);

  useEffect(() => {
    if (currentTZ && !selectedTimezone?.value) {
      setSelectedTimezone({ value: currentTZ || "" });
    }
  }, [currentTZ, selectedTimezone?.value]);

  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    if (Array.isArray(dealConfigureData) && dealConfigureData.length === 0) {
      if (dealStageRuleData && Object.keys(dealStageRuleData).length > 0) {
        const {
          deal_stage_from,
          deal_stage_to,
          follow_up_time,
          deal_template_id,
          stage_transition_time,
          deal_stage_to_name,
        } = dealStageRuleData;

        setSelectedToStage({
          stage_id: deal_stage_to,
          stage_name: deal_stage_to_name,
        });
        setFormState((prev) => ({
          ...prev,
          selectedEmailTemplate: String(deal_template_id),
          sendEmailAfterValue: parseTimeUnit(follow_up_time).num,
          sendEmailAfterUnit: parseTimeUnit(follow_up_time).unit,
          moveAfterValue: parseTimeUnit(stage_transition_time).num,
          moveAfterUnit: parseTimeUnit(String(stage_transition_time)).unit,
        }));
        setEmailConfigData({ ...emailConfigData, template: deal_template_id });
      }
    }
  }, [dealConfigureData]);

  const {
    moveAfterUnit,
    moveAfterValue,
    sendEmailAfterUnit,
    sendEmailAfterValue,
    selectedEmailTemplate,
    senderName,
  } = formState;

  const handleAddInputSet = () => {
    setSaving(true);
    const formattedAutoMoveTime = `${moveAfterValue}${
      moveAfterUnit === "days"
        ? "d"
        : moveAfterUnit === "hours"
        ? "h"
        : moveAfterUnit === "minute"
        ? "m"
        : ""
    }`;

    const formattedFollowUpTime = `${sendEmailAfterValue}${
      sendEmailAfterUnit === "days"
        ? "d"
        : sendEmailAfterUnit === "hours"
        ? "h"
        : "m"
    }`;

    const postData = {
      pipeline_id: dealPipelineId,
      deal_stage_from: currentDealStage,
      deal_stage_to: selectedToStage?.stage_id,
      follow_up_time: formattedFollowUpTime,
      time_zone: selectedTimezone?.label
        ? formatTimeZone(selectedTimezone)
        : getTimeZoneWithGMT(selectedTimezone),
      deal_id: dealId,
      // template_id: selectedEmailTemplate,
      template_id: emailConfigData?.template,
      ...(selectedDate && { stage_transition_time: formatDate(selectedDate) }),
      ...(formattedAutoMoveTime && {
        stage_transition_fallback_time: formattedAutoMoveTime,
      }),
      ...(emailConfigData?.toEmails?.length > 0 && {
        to_address: emailConfigData?.toEmails,
      }),
      ...(emailConfigData?.bccEmails?.length > 0 && {
        bcc_address: emailConfigData?.bccEmails,
      }),
      ...(emailConfigData?.ccEmails?.length > 0 && {
        cc_address: emailConfigData?.ccEmails,
      }),
      ...(senderName && { sender_name: senderName }),
    };

    const formData = new FormData();
    formData.append("data", JSON.stringify(postData));
    attachments?.forEach((file) => formData.append("attachments[]", file));

    dispatch(postConfigureStageTransfer(formData))
      .then((res) => {
        const { is_deal_available } = res;
        if (is_deal_available) {
          setOverRideOpen(true);
          setSaving(false);
        } else {
          dispatch(setAlert(`Deal configured successfully`, "success"));
          setSaving(false);
          setAttachments([]);
          setCount((prev) => prev + 1);
          dispatch(getDealStageOverRideView(deal?.deal_id))
            .then((res) => {
              setDealStageRuleData(res?.result?.rule || {});
              setIsCollapsed(false);
              setRenderDeal((prev) => prev + 1);
              setEmailConfigData({});
            })
            .catch(() => {
              dispatch(setAlert("Failed to fetch updated deal data", "error"));
            });
        }
      })
      .catch((error) => {
        dispatch(setAlert(error?.message || `Error in adding`, "error"));
        setSaving(false);
      });
  };

  const handleEdit = (index) => {
    setIsCollapsed(true);
    const setToEdit = dealConfigureData[index];
    const {
      deal_stage_from,
      deal_stage_to,
      time_frame,
      time_zone,
      id,
      deal_from_name,
      deal_to_name,
      template_id,
      stage_transition_time,
      follow_up_time,
      stage_transition_fallback_time,
      sender_name,
      attachments,
    } = setToEdit;

    const isInvalidStageTime =
      !stage_transition_time || stage_transition_time === null;

    // Make sure selectedDate is null if invalid
    const dateValue = isInvalidStageTime
      ? null
      : convertUTCToTimezoneNormalFormat(stage_transition_time, currentTZ); // Convert valid DB date to dayjs
    setEditId(id);
    setSelectedFromStage({
      stage_id: deal_stage_from,
      stage_name: deal_from_name,
    });
    setSelectedToStage({
      stage_id: deal_stage_to,
      stage_name: deal_to_name,
    });
    setSelectedTimezone({ value: extractTimezone(time_zone) });
    setTimezoneFormat({ value: time_zone });
    setSelectedDate(dateValue);
    setErrorMessage("");
    setFormState((prev) => ({
      ...prev,
      selectedEmailTemplate: String(template_id),
      sendEmailAfterValue: parseTimeUnit(follow_up_time).num,
      sendEmailAfterUnit: parseTimeUnit(follow_up_time).unit,
      moveAfterValue: parseTimeUnit(stage_transition_fallback_time).num,
      moveAfterUnit: parseTimeUnit(String(stage_transition_fallback_time)).unit,
      senderName: sender_name,
    }));
    setEmailConfigData({ ...emailConfigData, template: template_id });
    setExistingFiles(attachments);
  };

  const hasEmailAfter =
    formState?.sendEmailAfterValue && formState?.sendEmailAfterUnit;

  const hasMoveAfter = formState?.moveAfterValue && formState?.moveAfterUnit;

  const isFormValid =
    selectedToStage?.stage_id &&
    selectedTimezone?.value &&
    emailConfigData?.template &&
    hasEmailAfter &&
    (selectedDate || hasMoveAfter);

  const initialLoad = React.useRef(true);
  const handleUpdate = () => {
    setSaving(true);
    if (editId) {
      const formattedAutoMoveTime = `${moveAfterValue}${
        moveAfterUnit === "days"
          ? "d"
          : moveAfterUnit === "hours"
          ? "h"
          : moveAfterUnit === "minute"
          ? "m"
          : ""
      }`;
      const formattedFollowUpTime = `${sendEmailAfterValue}${
        sendEmailAfterUnit === "days"
          ? "d"
          : sendEmailAfterUnit === "hours"
          ? "h"
          : "m"
      }`;

      const updateData = {
        pipeline_id: dealPipelineId,
        deal_stage_from: currentDealStage,
        deal_stage_to: selectedToStage?.stage_id,
        follow_up_time: formattedFollowUpTime || "1d",
        time_zone: selectedTimezone?.label
          ? formatTimeZone(selectedTimezone)
          : timeZoneFormat?.value,
        deal_id: dealId,
        // template_id: selectedEmailTemplate,
        template_id: emailConfigData?.template,
        ...(selectedDate && {
          stage_transition_time: formatDate(selectedDate),
        }),
        ...(formattedAutoMoveTime && {
          stage_transition_fallback_time: formattedAutoMoveTime,
        }),
        ...(emailConfigData?.toEmails?.length > 0 && {
          to_address: emailConfigData?.toEmails,
        }),
        ...(emailConfigData?.bccEmails?.length > 0 && {
          bcc_address: emailConfigData?.bccEmails,
        }),
        ...(emailConfigData?.ccEmails?.length > 0 && {
          cc_address: emailConfigData?.ccEmails,
        }),
        sender_name: senderName,
        ...(deletedFileIds?.length > 0 && {
          file_delete_id: deletedFileIds,
        }),
        tbl_id: editId,
      };

      const formData = new FormData();
      formData.append("data", JSON.stringify(updateData));
      attachments?.forEach((file) => formData.append("attachments[]", file));

      dispatch(updateConfigureStageTransfer(formData))
        .then(() => {
          setCount(count + 1);
          setSelectedFromStage({ stage_id: "", stage_name: "" });
          setSelectedToStage({ stage_id: "", stage_name: "" });
          setSelectedDate(null);
          setErrorMessage("");
          setSaving(false);
          setSelectedTimezone({ value: "" });
          setEmailConfigData({});
          setAttachments([]);
          dispatch(getDealStageOverRideView(deal?.deal_id)).then((res) => {
            setDealStageRuleData(res?.result?.rule || {});
            setIsCollapsed(false);
            setRenderDeal(renderDeal + 1);
          });
        })
        .catch((error) => {
          setSaving(false);
          dispatch(
            setAlert(
              error?.response?.data?.message ||
                `Failed to update deal configuration`,
              "error"
            )
          );
        });
    }
  };

  useEffect(() => {
    // Show loader for 2 seconds
    const timer = setTimeout(() => {
      setLoadingLinear(false); // Stop the loader after 2 seconds
    }, 300);

    // Cleanup timer on component unmount
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const isStageChanged = dealConfigureData?.some(
      (item) => item?.deal_stage_from !== currentDealStage
    );
    setSameStageCheck(isStageChanged);
  }, [currentDealStage, dealConfigureData]);

  const onOverRideTimeFrame = (e) => {
    e.preventDefault();
    const formattedAutoMoveTime = `${moveAfterValue}${
      moveAfterUnit === "days"
        ? "d"
        : moveAfterUnit === "hours"
        ? "h"
        : moveAfterUnit === "minute"
        ? "m"
        : ""
    }`;

    const formattedFollowUpTime = `${sendEmailAfterValue}${
      sendEmailAfterUnit === "days"
        ? "d"
        : sendEmailAfterUnit === "hours"
        ? "h"
        : "m"
    }`;
    const postData = {
      pipeline_id: dealPipelineId,
      deal_stage_from: currentDealStage,
      deal_stage_to: selectedToStage?.stage_id,
      follow_up_time: formattedFollowUpTime,
      time_zone: selectedTimezone?.label
        ? formatTimeZone(selectedTimezone)
        : getTimeZoneWithGMT(selectedTimezone),
      deal_id: dealId,
      // template_id: selectedEmailTemplate,
      template_id: emailConfigData?.template,
      ...(selectedDate && { stage_transition_time: formatDate(selectedDate) }),
      ...(formattedAutoMoveTime && {
        stage_transition_fallback_time: formattedAutoMoveTime,
      }),
      confirm: true,
      ...(emailConfigData?.toEmails?.length > 0 && {
        to_address: emailConfigData?.toEmails,
      }),
      ...(emailConfigData?.bccEmails?.length > 0 && {
        bcc_address: emailConfigData?.bccEmails,
      }),
      ...(emailConfigData?.ccEmails?.length > 0 && {
        cc_address: emailConfigData?.ccEmails,
      }),
      ...(senderName && { sender_name: senderName }),
    };

    const formData = new FormData();
    formData.append("data", JSON.stringify(postData));
    attachments?.forEach((file) => formData.append("attachments[]", file));

    dispatch(postConfigureStageTransfer(formData))
      .then((res) => {
        setOverRideOpen(false);
        setCount(count + 1);
        dispatch(setAlert(`Deal configured successfully`, "success"));
        setEmailConfigData({});
        setAttachments([]);
        dispatch(getDealStageOverRideView(deal?.deal_id)).then((res) => {
          setDealStageRuleData(res?.result?.rule || {});
          setIsCollapsed(false);
          setRenderDeal(renderDeal + 1);
        });
      })
      .catch((error) => {
        setSaving(false);
        dispatch(
          setAlert(
            error?.response?.data?.message ||
              `Failed to create deal configuration`,
            "error"
          )
        );
      });
  };

  const handleSendMailChange = (event) => {
    setIsSendMail(event.target.checked);
    if (!isFormModified) {
      setIsFormModified(true);
    }
  };
  const isEditOrCreate =
    isCollapsed || !dealConfigureData || dealConfigureData.length === 0;

  const onEmailtempOpen = (stage) => {
    setOpenTemplate(true);
  };

  const stage = { stage_id: deal?.deal_stage };

  const handleFormChange = (key, value) => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
    setIsFormModified(true);
  };

  return (
    <>
      {loadingLinear ? (
        <LinearProgress />
      ) : (
        <Fragment>
          <DealEmailTemplate
            open={openTemplate}
            attachments={attachments}
            setAttachments={setAttachments}
            close={() => setOpenTemplate(false)}
            lead={{}}
            loadingDU={loading}
            multiple
            stage={stage}
            bcc={true}
            setEmailConfigData={setEmailConfigData}
            dealDetails={{
              ...dealDetails,
              ...(dealConfigureData?.length > 0 && {
                to_address: dealConfigureData[0]?.to_address,
                cc_address: dealConfigureData[0]?.cc_address,
                bcc_address: dealConfigureData[0]?.bcc_address,
              }),
            }}
          />
          <div style={{ overflow: "hidden" }}>
            {isEditOrCreate ? (
              // crate , edit
              <Fragment>
                <Typography
                  sx={{ marginTop: 1, marginBottom: 1 }}
                  variant="body2"
                  fontWeight="bold"
                >
                  Timezone
                </Typography>
                <TimezoneSelect
                  value={selectedTimezone}
                  onChange={(timezone) => {
                    setSelectedTimezone(timezone);
                    setIsFormModified(true);
                  }}
                  placeholder="Select Timezone"
                  menuPlacement="bottom"
                  styles={{
                    marginBottom: "5px",
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999, // Increase z-index to ensure it appears on top
                    }),
                  }}
                />
                <Divider style={{ margin: 15 }} />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ marginTop: 1, marginBottom: 1 }}
                >
                  Automatic Stage Transfer
                </Typography>

                {/* New Input Section for To Stage */}
                <Select
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={selectedToStage?.stage_id || "select"}
                  sx={{ marginBottom: 1 }}
                  onChange={(e) => {
                    const selectedItem = fromStage?.find(
                      (item) => item.stage_id === e.target.value
                    );
                    handleStageChange(
                      "to",
                      selectedItem.stage_id,
                      selectedItem.stage_name
                    );
                  }}
                >
                  <MenuItem value="select">
                    ------ Move To Stage ------
                  </MenuItem>
                  {fromStage?.map((item, index) => (
                    <MenuItem key={index} value={item.stage_id}>
                      {item.stage_name}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
                  Auto-Move — Choose <strong>either</strong> Time or Date & Time
                </Typography>

                {/* Time Option */}
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="Move After"
                    type="number"
                    size="small"
                    fullWidth
                    value={formState.moveAfterValue || ""}
                    onChange={(e) => {
                      handleFormChange("moveAfterValue", e.target.value);
                      setSelectedDate(null); // Reset date when time is chosen
                      setTouched(true);
                    }}
                    disabled={!!selectedDate}
                    sx={{ marginTop: "10px" }}
                    error={
                      touched && !formState.moveAfterValue && !selectedDate
                    }
                    inputProps={{
                      min: 1,
                      onWheel: (e) => e.target.blur(),
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "-" || // prevent negative
                        e.key === "e" || // prevent scientific notation
                        e.key === "." || // prevent decimals
                        (e.key === "0" && e.target.value.length === 0) // prevent starting with 0
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <Select
                    style={{ marginTop: "10px" }}
                    size="small"
                    displayEmpty
                    value={formState.moveAfterUnit || ""}
                    onChange={(e) => {
                      handleFormChange("moveAfterUnit", e.target.value);
                      setSelectedDate(null); // Reset date when time is chosen
                      setTouched(true);
                    }}
                    disabled={!!selectedDate}
                    error={touched && !formState.moveAfterUnit && !selectedDate}
                  >
                    <MenuItem value="" disabled>
                      Select Unit
                    </MenuItem>
                    <MenuItem value="days">Day</MenuItem>
                    <MenuItem value="hours">Hour</MenuItem>
                    <MenuItem value="minute">Minute</MenuItem>
                  </Select>
                </Stack>

                {/* Date & Time Option */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Move To Stage After (Date & Time)"
                    value={selectedDate}
                    onChange={(newValue) => {
                      setSelectedDate(newValue);
                      handleFormChange("moveAfterValue", "");
                      handleFormChange("moveAfterUnit", "");
                    }}
                    disabled={!!formState.moveAfterValue}
                    minDateTime={dayjs()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        margin="dense"
                        sx={{ mt: 2 }}
                      />
                    )}
                  />
                </LocalizationProvider>
                <div style={{ maxWidth: "500px" }}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mb: 1, fontSize: "12px" }}
                  >
                    Set when the deal should automatically move to the selected
                    stage. You can either:
                    <ul style={{ margin: 0, paddingLeft: "16px" }}>
                      <li>
                        Specify a duration, such as <strong>1 day</strong>,{" "}
                        <strong>3 hours</strong>, or <strong>30 minutes</strong>
                        .
                      </li>
                      <li>
                        Pick an exact <strong>Date & Time</strong> for the move.
                      </li>
                    </ul>
                  </Typography>
                </div>
                <Divider style={{ margin: 15 }} />
                <div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      maxWidth: "500px",
                    }}
                  >
                    <Typography variant="caption" fontWeight="bold">
                      Follow-Up Email Settings
                    </Typography>
                    <Typography
                      sx={{ mt: 1, fontSize: "12px" }}
                      variant="caption"
                      color="textSecondary"
                    >
                      After the deal moved to selected stage, the{" "}
                      <strong>follow-up email</strong> will be sent
                      automatically based on the schedule below.
                    </Typography>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {emailConfigData && (
                      <div className="summary">
                        {emailConfigData?.toEmails?.length > 0 && (
                          <p>
                            <b>To:</b> {emailConfigData?.toEmails.join(", ")}
                          </p>
                        )}
                        {emailConfigData?.ccEmails?.length > 0 && (
                          <p>
                            <b>Cc:</b> {emailConfigData?.ccEmails?.join(", ")}
                          </p>
                        )}
                        {emailConfigData?.bccEmails?.length > 0 && (
                          <p>
                            <b>Bcc:</b> {emailConfigData?.bccEmails?.join(", ")}
                          </p>
                        )}
                      </div>
                    )}
                    {emailConfigData?.template && (
                      <>
                        <Typography variant="caption" fontWeight="bold">
                          Selected Email Template
                        </Typography>
                        <Select
                          size="small"
                          displayEmpty
                          disabled
                          renderValue={(selected) => {
                            if (!selected) {
                              return <span>Select Email Template</span>;
                            }
                            const template = bodyTemplates?.find(
                              (t) => String(t.tb_id) === selected
                            );
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography>{template?.name || ""}</Typography>
                                <Box
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color:
                                      template?.is_plain_email === "1"
                                        ? "#2e7d32"
                                        : "#1565c0",
                                    backgroundColor:
                                      template?.is_plain_email === "1"
                                        ? "#E8F5E9"
                                        : "#E3F2FD",
                                    borderRadius: "10px",
                                    px: 1,
                                    py: "2px",
                                  }}
                                >
                                  {template.is_plain_email === "1"
                                    ? "Plain"
                                    : "Branded"}
                                </Box>
                              </Box>
                            );
                          }}
                          // value={formState?.selectedEmailTemplate || ""}
                          value={String(emailConfigData?.template)}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "add_new_template") {
                              onEmailtempOpen(stage);
                            } else {
                              handleFormChange("selectedEmailTemplate", value);
                            }
                          }}
                        >
                          {bodyTemplates?.length > 0
                            ? [
                                <MenuItem key="placeholder" value="" disabled>
                                  Select Email Template
                                </MenuItem>,
                                <MenuItem
                                  value="add_new_template"
                                  sx={{ color: "#1976d2" }}
                                >
                                  <AddCircleOutline
                                    fontSize="small"
                                    sx={{ mr: 1 }}
                                  />{" "}
                                  Add New Template
                                </MenuItem>,
                                <MenuItem disabled>
                                  <hr
                                    style={{
                                      width: "100%",
                                      border: "none",
                                      borderTop: "1px solid #ddd",
                                    }}
                                  />
                                </MenuItem>,
                                ...bodyTemplates.map((template) => (
                                  <MenuItem
                                    key={template.id}
                                    value={String(template.tb_id)}
                                  >
                                    {template.name}
                                  </MenuItem>
                                )),
                              ]
                            : [
                                <MenuItem key="not-found" disabled>
                                  No templates found
                                </MenuItem>,
                              ]}
                        </Select>
                      </>
                    )}

                    <TextField
                      label="Custom Sender Name"
                      type="text"
                      size="small"
                      placeholder="Override stage default sender name"
                      value={formState.senderName || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        const filteredValue = value
                          .replace(/[^a-zA-Z\s]/g, "")
                          .slice(0, 50);
                        handleFormChange("senderName", filteredValue);
                      }}
                      style={{ width: "100%" }}
                      inputProps={{
                        maxLength: 50,
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <TextField
                        label="Send Email After"
                        type="number"
                        size="small"
                        value={formState.sendEmailAfterValue || ""}
                        onChange={(e) =>
                          handleFormChange(
                            "sendEmailAfterValue",
                            e.target.value
                          )
                        }
                        style={{ width: "100%" }}
                        inputProps={{
                          min: 1,
                          onWheel: (e) => e.target.blur(),
                        }}
                        onKeyDown={(e) => {
                          if (
                            e.key === "-" || // prevent negative
                            e.key === "e" || // prevent scientific notation
                            e.key === "." || // prevent decimals
                            (e.key === "0" && e.target.value.length === 0) // prevent starting with 0
                          ) {
                            e.preventDefault();
                          }
                        }}
                      />
                      <Select
                        sx={{
                          width: "30%",
                        }}
                        displayEmpty
                        size="small"
                        value={formState.sendEmailAfterUnit || ""}
                        onChange={(e) =>
                          handleFormChange("sendEmailAfterUnit", e.target.value)
                        }
                        error={
                          Boolean(formState.sendEmailAfterValue) &&
                          !formState.sendEmailAfterUnit
                        }
                      >
                        <MenuItem value="" disabled>
                          Select Unit
                        </MenuItem>
                        <MenuItem value="days">Day</MenuItem>
                        <MenuItem value="hours">Hour</MenuItem>
                        <MenuItem value="minute">Minute</MenuItem>
                      </Select>
                    </Box>
                  </div>
                </div>

                <Typography
                  sx={{ mt: 1 }}
                  variant="body2"
                  color="textSecondary"
                >
                  Configure the email settings for this deal —{" "}
                  <Typography
                    component="span"
                    color="primary"
                    sx={{ textDecoration: "underline", cursor: "pointer" }}
                    onClick={() => onEmailtempOpen(stage)}
                  >
                    Configure Email
                  </Typography>
                </Typography>

                <Box sx={{ marginTop: "10px", marginBottom: "6px" }}>
                  <FileUploader
                    attachments={attachments}
                    setAttachments={setAttachments}
                    existingFiles={existingFiles}
                    setExistingFiles={setExistingFiles}
                    setDeletedFileIds={setDeletedFileIds}
                  />
                </Box>
                <Typography
                  sx={{ fontSize: "12px" }}
                  variant="caption"
                  color="primary"
                >
                  Attach any files you’d like to include with the follow-up
                  email
                </Typography>
                <Divider style={{ margin: 15 }} />
                {/* Display error message if any field is missing */}
                {errorMessage && (
                  <Typography color="error" sx={{ marginBottom: 2 }}>
                    {errorMessage}
                  </Typography>
                )}
                <div
                  className="dealOptionsWrapper"
                  style={{ display: "flex", gap: "5px" }}
                >
                  <Button color="primary" onClick={cancel}>
                    Cancel
                  </Button>
                  <Divider orientation="vertical" flexItem />
                  <Button
                    color="primary"
                    onClick={editId !== null ? handleUpdate : handleAddInputSet}
                    disabled={!isFormValid || saving} // disable when saving
                    startIcon={
                      saving ? (
                        <CircularProgress size={18} color="primary" />
                      ) : editId !== null ? null : null
                    }
                  >
                    {saving
                      ? editId !== null
                        ? "Updating..."
                        : "Saving..."
                      : editId !== null
                      ? "Update"
                      : "Save"}
                  </Button>
                </div>
              </Fragment>
            ) : (
              // view
              <Fragment>
                {dealConfigureData &&
                  dealConfigureData?.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        marginBottom: 2,
                        padding: "16px",
                        border: "1px solid #ddd",
                        borderRadius: 4,
                      }}
                    >
                      <Typography variant="body1" mb={0.5}>
                        <strong>From Stage :</strong> {item?.deal_from_name}
                      </Typography>
                      <Typography variant="body1" mb={0.5}>
                        <strong>To Stage :</strong> {item?.deal_to_name}
                      </Typography>
                      <Typography variant="body1" mb={0.5}>
                        <strong>Timezone :</strong> {item?.time_zone}
                      </Typography>
                      {item?.stage_transition_time && (
                        <Typography variant="body1" mb={0.5}>
                          <strong>Date :</strong>{" "}
                          {convertUTCToTimezone(
                            item?.stage_transition_time,
                            currentTZ
                          )}
                        </Typography>
                      )}
                      <Typography variant="body1" mb={0.5}>
                        <strong>Email Template Name :</strong>{" "}
                        {item?.template_name}
                      </Typography>
                      {item?.stage_transition_fallback_time && (
                        <Typography variant="body1" mb={0.5}>
                          <strong>Move to Stage After Time :</strong>{" "}
                          {convertUTCToTimezone(
                            item?.original_stage_transition_time,
                            currentTZ
                          )}
                        </Typography>
                      )}
                      {item?.follow_up_time && (
                        <Typography variant="body1" mb={0.5}>
                          <strong>Follow up Time :</strong>{" "}
                          {convertUTCToTimezone(item?.schedule_time, currentTZ)}
                        </Typography>
                      )}
                      {item?.sender_name && (
                        <Typography variant="body1" mb={0.5}>
                          <strong>Custom Sender Name :</strong>{" "}
                          {item?.sender_name}
                        </Typography>
                      )}
                      {item?.attachments?.length > 0 && (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: "bold",
                              flexShrink: 0, // keeps it fixed
                            }}
                          >
                            Files :
                          </Typography>

                          {/* Scrollable attachments section */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              overflowX: "auto",
                              overflowY: "hidden",
                              flexGrow: 1,
                              gap: 1,
                              "&::-webkit-scrollbar": {
                                height: 6,
                              },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "rgba(0,0,0,0.3)",
                                borderRadius: "4px",
                              },
                              pb: 0.5,
                            }}
                          >
                            {item?.attachments?.map((file, index) => (
                              <Chip
                                key={`existing-${file.id || index}`}
                                icon={<InsertDriveFileIcon />}
                                size="small"
                                label={file.file_name}
                                onClick={() => {
                                  if (file?.file_path) {
                                    const link = document.createElement("a");
                                    link.href = file?.file_path;
                                    link.target = "_blank";
                                    link.rel = "noopener noreferrer";
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }
                                }}
                                sx={{
                                  maxWidth: 200,
                                  textOverflow: "ellipsis",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  flexShrink: 0,
                                }}
                              />
                            ))}
                          </Box>
                        </Stack>
                      )}

                      <Button
                        onClick={() => handleEdit(index)}
                        variant="outlined"
                        sx={{ marginTop: 1 }}
                      >
                        Edit
                      </Button>
                    </Box>
                  ))}
              </Fragment>
            )}
            <Dialog
              open={overRideOpen}
              onClose={() => setOverRideOpen(false)}
              maxWidth="xs"
            >
              <DialogContent>
                <Typography className="warning-container">
                  Do you want to override individual deal timelines
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOverRideOpen(false)} color="primary">
                  Cancel
                </Button>
                <Button
                  style={{ color: "red" }}
                  onClick={onOverRideTimeFrame}
                  autoFocus
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        </Fragment>
      )}
    </>
  );
};

export default DealConfigure;
